import { useQuery } from "@apollo/client";
import Drawer from "@mui/material/Drawer";
import { Button, Card, Icon } from "pepsico-ds";
import { useMemo, useState } from "react";
import { getDateWithFormat } from "../../../utils/commons";
import { toCamelCase } from "../../../utils/functions";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import getListPartners from "../graphql/queries/listPartners";
import "./partnerCodeGroupSummary.scss";

const DETAILS_TITLE = "Partner Code Group Details";
const EDITABLE_STATUSES = new Set(["published", "paused", "inactive", "active"]);

const DetailsField = ({ label, children }) => (
  <Card border ellipsis={false} className="pcg-details-field">
    <span className="pcg-details-field-label">{label}</span>
    <div className="pcg-details-field-value">{children}</div>
  </Card>
);

function PartnerCodeGroupSummaryModal({
  PartnerCodeGroupSummaryModalOpen,
  toggleSummaryModal,
  handleActionButtons,
  partnerCodeGroupData,
  currentProgramTimezone,
}) {
  const [toast, setToast] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
  });

  // Fetch active partners
  const { data: listOfPartnersData } = useQuery(getListPartners, {
    variables: { filter: {} },
  });

  // Memoize the list of partners to avoid unnecessary re-renders
  const partners = useMemo(
    () => listOfPartnersData?.listPartners?.items || [],
    [listOfPartnersData]
  );

  const getPartnerName = (partnerId) => {
    const partner = partners.find((partner) => partner.id === partnerId);
    return partner?.name || "-";
  };

  const canEdit = EDITABLE_STATUSES.has(partnerCodeGroupData?.status?.toLowerCase());

  return (
    <Drawer
      open={PartnerCodeGroupSummaryModalOpen}
      onClose={toggleSummaryModal}
      anchor="right"
      classes={{ paper: "pcg-details-drawer" }}
    >
      <div className="pcg-details-drawer-header">
        <h2 className="pcg-details-drawer-title">{DETAILS_TITLE}</h2>
        <button
          type="button"
          aria-label="Close"
          className="pcg-details-close-button"
          onClick={toggleSummaryModal}
        >
          <Icon icon="close" />
        </button>
      </div>

      <div className="pcg-details-drawer-content">
        <div className="pcg-details-section">
          <h3 className="pcg-details-section-title">Partner Code Group Details</h3>
          <DetailsField label="PARTNER CODE">
            {getPartnerName(partnerCodeGroupData?.partnerId)}
          </DetailsField>
          <DetailsField label="GROUP NAME">
            {partnerCodeGroupData?.name || "-"}
          </DetailsField>
          {partnerCodeGroupData?.codeGroupType && (
            <DetailsField label="CODE GROUP">
              {toCamelCase(partnerCodeGroupData.codeGroupType) || "-"}
            </DetailsField>
          )}
          {partnerCodeGroupData?.codeGroupType === "UNLIMITED_CODE" && (
            <DetailsField label="UNLIMITED CODE / LINK">
              {partnerCodeGroupData?.singleCode || "-"}
            </DetailsField>
          )}
          <DetailsField label="DESCRIPTION">
            {partnerCodeGroupData?.description || "-"}
          </DetailsField>
          <DetailsField label="ACTIVE DATES">
            <p>
              {partnerCodeGroupData?.startDate
                ? getDateWithFormat(
                    partnerCodeGroupData.startDate,
                    currentProgramTimezone
                  )
                : "N/A"}
              {" - "}
              {partnerCodeGroupData?.endDate
                ? getDateWithFormat(
                    partnerCodeGroupData.endDate,
                    currentProgramTimezone
                  )
                : "N/A"}
            </p>
          </DetailsField>
        </div>

        <div className="pcg-details-section">
          <h3 className="pcg-details-section-title">Partner Code Group Details</h3>
          <DetailsField label="FILE NAME">
            {partnerCodeGroupData?.rewardItemFileData?.length > 0
              ? partnerCodeGroupData.rewardItemFileData
                  .map((file) => file?.name?.split("/")?.pop())
                  .join(", ")
              : "-"}
          </DetailsField>
          <DetailsField label="IMPORT DATE">
            {partnerCodeGroupData?.processingStatus === "IMPORTED"
              ? getDateWithFormat(
                  partnerCodeGroupData?.importedDate,
                  currentProgramTimezone
                )
              : "Processing"}
          </DetailsField>
          <DetailsField label="FILE STATUS">
            {partnerCodeGroupData?.processingStatus === "IMPORTED" ? (
              <Button
                size="small"
                variant="primary"
                iconTrailing="done"
                className="code-imported"
              >
                Code Imported
              </Button>
            ) : (
              "Not Imported"
            )}
          </DetailsField>
        </div>
      </div>

      <div className="pcg-details-drawer-footer">
        <Button size="medium" variant="secondary" onClick={toggleSummaryModal}>
          Cancel
        </Button>
        {canEdit && (
          <Button
            size="medium"
            variant="primary"
            disabled={partnerCodeGroupData?.processingStatus === "IMPORTING"}
            onClick={() => {
              toggleSummaryModal();
              handleActionButtons("edit", partnerCodeGroupData);
            }}
          >
            Save
          </Button>
        )}
      </div>

      <ToastNotification
        {...toast}
        handleClose={() => setToast({ ...toast, open: false })}
      />
    </Drawer>
  );
}

export default PartnerCodeGroupSummaryModal;
